/**
 * Hero image pool + shuffle helper.
 *
 * Deliberately not exported from HeroImageCarousel.tsx: that file is a
 * "use client" module, and Hero.tsx (which shuffles this pool per request —
 * see below) is a Server Component. A Server Component importing any export
 * — including a plain array — from a "use client" module gets a
 * client-reference proxy, not the real value, which has already silently
 * broken this codebase once (see heroLayout.ts). This module has no
 * directive, so it's safe to import from either side of the boundary.
 *
 * To add images: drop files in public/marketing/ named hero-<n>.jpg
 * (e.g. hero-1.jpg, hero-2.jpg) and add each entry here.
 * Next.js cannot enumerate public/ at runtime, so only listed files are shown.
 * Also add the file to QUIZ_IMAGES in scripts/quizImageList.mjs and re-run
 * scripts/compress-images.mjs then scripts/gen-blur-data.mjs.
 *
 * Crop math: the carousel container (Hero.tsx, sized via HERO_ASPECT in
 * ./heroLayout) is a fixed aspect-[4/5] (0.8) box and every slide renders
 * `object-cover`,
 * so a portrait image of ratio `r` only shows `r / 0.8` of its height (a
 * landscape image of ratio `r` only shows `0.8 / r` of its width) — the rest
 * is cropped from both edges around the default center. `objectPosition`
 * overrides that default crop only where it would otherwise clip the subject
 * (e.g. a face or branded cap); leave it unset when centering is fine.
 *
 * sizes: `object-fit: cover` scales each image by
 * `max(containerW / naturalW, containerH / naturalH)`. Every image in the
 * current pool is portrait with ratio ≤ 0.8 (the container's own aspect
 * ratio), so that scale is always width-bound and lands on the container's
 * 540px width — the default `sizes` below is correct for the whole pool. If
 * a future addition has ratio > 0.8 (wider than the box, i.e. closer to
 * square), it becomes height-bound and needs a wider `sizes` override —
 * `HeroImage.sizes` exists for exactly that case; see hero-15/hero-18 in the
 * git history of this file for a worked example.
 */
const DEFAULT_SIZES = "(max-width: 580px) 100vw, 540px";

export type HeroImage = {
  src: string;
  alt: string;
  objectPosition?: `${number}% ${number}%`;
  sizes?: string;
};

export const HERO_IMAGES: HeroImage[] = Array.from({ length: 40 }, (_, i) => ({
  src: `/marketing/hero-${i + 1}.jpg`,
  alt: "",
}));

export { DEFAULT_SIZES };

/**
 * Returns a new array containing a random permutation of `images` (Fisher–
 * Yates, using the injected `rng` so callers can make it deterministic in
 * tests). When `previousLast` is given and the shuffled result's first
 * element has that `src`, one swap is performed against a different index so
 * the same image is never shown twice in a row across a reshuffle boundary.
 *
 * `rng` must return a value in `[0, 1)` — matching `Math.random()`'s
 * contract. A value of exactly `1` will index out of bounds.
 *
 * `images.length < 2` is a degenerate case — the no-repeat constraint can't
 * be satisfied (or is meaningless), so the input is returned as a new array
 * with no swap attempted.
 */
export function shuffleHeroImages(
  images: readonly HeroImage[],
  rng: () => number = Math.random,
  previousLast?: string,
): HeroImage[] {
  const result = [...images];

  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [result[i], result[j]] = [result[j]!, result[i]!];
  }

  if (result.length >= 2 && previousLast && result[0]!.src === previousLast) {
    // Swap slot 0 with some other slot. Any of them is safe: the only
    // forbidden arrangement is index 0 === previousLast, and swapping moves
    // that src away from index 0 without re-introducing it elsewhere.
    const swapWith = 1 + Math.floor(rng() * (result.length - 1));
    [result[0], result[swapWith]] = [result[swapWith]!, result[0]!];
  }

  return result;
}
